import { getApiPublicBase } from '../utils/apiPublicBase';

const API_BASE = getApiPublicBase();

// 辅助函数：获取token
const getToken = () => {
  return localStorage.getItem('token');
};

// 辅助函数：API请求
const apiRequest = async (url: string, options: RequestInit = {}) => {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
      throw new Error('未授权，请重新登录');
    }
    const error = await response.json().catch(() => ({ message: '请求失败' }));
    throw new Error(error.message || error.error || '请求失败');
  }

  return response.json();
};

// 类型定义
export interface DutyRecord {
  images: string[]; // 值日图片URL数组（所有区域汇总）
  areaRecords?: {
    areaName: string;
    images: string[];
  }[]; // 按区域划分的图片记录（可选）
  uploadedAt: string; // 上传时间
  uploadedBy: string | {
    _id: string;
    name: string;
    username?: string;
  }; // 上传人
  description?: string; // 值日记录描述
}

export interface DutyMember {
  userId: string | {
    _id: string;
    name: string;
    username?: string;
    avatar?: string;
    studentId?: string;
  };
  dutyType: '值日' | '值班';
  location?: string; // 值日地点
  tasks: string[];
  status: '待完成' | '进行中' | '已完成' | '未完成';
  checkInTime?: string;
  checkOutTime?: string;
  notes?: string;
  dutyRecords?: DutyRecord[]; // 值日记录数组
}

export interface DutyDay {
  date: string;
  dayOfWeek: number;
  location?: string; // 值日地点（日期级别）
  members: DutyMember[];
}

// 值日成员组
export interface DutyGroup {
  _id: string;
  name: string;
  description?: string;
  memberIds: {
    _id: string;
    name: string;
    username?: string;
    studentId?: string;
    avatar?: string;
  }[];
}

export interface DutySchedule {
  _id?: string;
  week: string;
  weekStartDate: string;
  weekEndDate: string;
  schedule: DutyDay[];
  createdBy: string | {
    _id: string;
    name: string;
    username?: string;
  };
  isActive: boolean;
  autoRotate: boolean;
  locationFrequencies?: Array<{
    location: string;
    frequency: number;
  }>;
  dutyAreasConfig?: Array<{
    location: string;
    areas: string[];
  }>;
  notificationSettings: {
    remindBeforeDay: number;
    remindOnDay: boolean;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateDutyScheduleData {
  week: string;
  weekStartDate: string;
  weekEndDate: string;
  schedule: DutyDay[];
  autoRotate?: boolean;
  notificationSettings?: {
    remindBeforeDay: number;
    remindOnDay: boolean;
  };
}

export interface AutoGenerateDutyScheduleData {
  weekStartDate: string;
  memberIds: string[];
  excludeDates?: string[];
  excludeHolidays?: boolean; // 排除法定节假日（不排除周末）
  excludeHolidaysAndWeekends?: boolean; // 排除法定节假日（且排除周末）
  excludeWeekends?: boolean; // 排除周末
}

export interface LocationConfig {
  location: string; // 值日地点名称
  memberIds: string[]; // 该地点的值日成员（按成员排班时使用）
  frequency?: number; // 值日频率（每x天一次，默认1表示每天，范围1-30）
  groupIds?: string[]; // 该地点的值日成员组（按组排班时使用）
  mode?: 'member' | 'group'; // 排班方式：按成员 / 按组
  requiredAreas?: string[]; // 该地点的必拍区域列表（例如：地面、操作台等）
  scheduleMode?: 'frequency' | 'weeklyFixed'; // 排班模式：按频次 / 按每周固定
  weeklyDays?: number[]; // 每周固定排班的星期几（0=周日,...,6=周六）
}

export interface BatchAutoGenerateDutyScheduleData {
  startWeek: string; // 开始周（周一日期）
  endWeek: string; // 结束周（周日日期）
  locationConfigs: LocationConfig[]; // 地点配置数组
  excludeDates?: string[]; // 排除的日期
  excludeHolidays?: boolean; // 排除法定节假日（不排除周末）
  excludeHolidaysAndWeekends?: boolean; // 排除法定节假日（且排除周末）
  excludeWeekends?: boolean; // 排除周末
}

export interface DutyStats {
  total: number;
  completed: number;
  uncompleted: number;
  completionRate: string;
  userStats: Array<{
    userId: string;
    userName: string;
    total: number;
    completed: number;
    uncompleted: number;
    averageRating: number;
  }>;
}

export interface DutyIncompleteRecord {
  _id: string;
  userId?: string | { _id: string; name?: string; username?: string; avatar?: string; studentId?: string };
  userName?: string;
  username?: string;
  message: string;
  location?: string;
  dutyDate?: string;
  cycleStart?: string;
  cycleEnd?: string;
  frequency?: number;
  isGroupDuty?: boolean;
  dutyGroupName?: string;
  createdAt?: string;
}

export const dutyScheduleService = {
  // 获取所有值日表
  getList: async (params?: { page?: number; limit?: number; week?: string }): Promise<{ data: DutySchedule[]; total: number; page: number; limit: number }> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.week) queryParams.append('week', params.week);
    
    const response = await apiRequest(`/api/duty-schedules?${queryParams.toString()}`);
    return response.data;
  },

  // 获取当前周值日表
  getCurrent: async (): Promise<DutySchedule> => {
    const response = await apiRequest('/api/duty-schedules/current');
    return response.data;
  },

  // 获取指定周值日表
  getByWeek: async (week: string): Promise<DutySchedule> => {
    const response = await apiRequest(`/api/duty-schedules/week/${week}`);
    return response.data;
  },

  // 获取我的值日安排
  getMyDuties: async (params?: { startDate?: string; endDate?: string }): Promise<DutySchedule[]> => {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    
    const response = await apiRequest(`/api/duty-schedules/my?${queryParams.toString()}`);
    return response.data;
  },

  // 获取单个值日表详情
  getById: async (id: string): Promise<DutySchedule> => {
    const response = await apiRequest(`/api/duty-schedules/${id}`);
    return response.data;
  },

  // 创建值日表（保留接口，当前主要使用批量生成功能）
  create: async (data: CreateDutyScheduleData): Promise<DutySchedule> => {
    const response = await apiRequest('/api/duty-schedules', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  // 批量自动生成值日表（支持多周和不同地点）
  batchAutoGenerate: async (data: BatchAutoGenerateDutyScheduleData): Promise<{ data: DutySchedule[]; message: string }> => {
    const response = await apiRequest('/api/duty-schedules/batch-auto-generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  },

  // 更新值日表
  update: async (id: string, data: Partial<CreateDutyScheduleData>): Promise<DutySchedule> => {
    const response = await apiRequest(`/api/duty-schedules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  // 值日签到
  checkIn: async (scheduleId: string, date: string, memberIndex: number): Promise<DutySchedule> => {
    const response = await apiRequest(`/api/duty-schedules/${scheduleId}/checkin`, {
      method: 'PUT',
      body: JSON.stringify({ scheduleId, date, memberIndex }),
    });
    return response.data;
  },

  // 值日签退
  checkOut: async (scheduleId: string, date: string, memberIndex: number): Promise<DutySchedule> => {
    const response = await apiRequest(`/api/duty-schedules/${scheduleId}/checkout`, {
      method: 'PUT',
      body: JSON.stringify({ scheduleId, date, memberIndex }),
    });
    return response.data;
  },

  // 完成值日
  complete: async (scheduleId: string, date: string, memberIndex: number, notes?: string): Promise<DutySchedule> => {
    const response = await apiRequest(`/api/duty-schedules/${scheduleId}/complete`, {
      method: 'PUT',
      body: JSON.stringify({ scheduleId, date, memberIndex, notes }),
    });
    return response.data;
  },

  // 上传值日记录（支持按必拍区域上传）
  uploadRecord: async (
    scheduleId: string,
    date: string,
    memberIndex: number,
    images: string[],
    description?: string,
    areaRecords?: { areaName: string; images: string[] }[]
  ): Promise<DutySchedule> => {
    const response = await apiRequest(`/api/duty-schedules/${scheduleId}/upload-record`, {
      method: 'PUT',
      body: JSON.stringify({ scheduleId, date, memberIndex, images, description, areaRecords }),
    });
    return response.data;
  },

  // 删除值日表
  // scope: 'week' 仅删除本周 | 'weekAndAfter' 删除本周及本周以后的所有值日表
  delete: async (id: string, scope?: 'week' | 'weekAndAfter'): Promise<{ message?: string; deletedCount?: number }> => {
    const url = scope === 'weekAndAfter'
      ? `/api/duty-schedules/${id}?scope=weekAndAfter`
      : `/api/duty-schedules/${id}`;
    const response = await apiRequest(url, { method: 'DELETE' }) as { message?: string; deletedCount?: number };
    return { message: response?.message, deletedCount: response?.deletedCount };
  },

  // 获取值日统计
  getStats: async (params?: { startDate?: string; endDate?: string; userId?: string }): Promise<DutyStats> => {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.userId) queryParams.append('userId', params.userId);
    
    const response = await apiRequest(`/api/duty-schedules/stats?${queryParams.toString()}`);
    return response.data;
  },

  // 获取未完成值日记录（值日表下方列表）
  getIncompleteRecords: async (): Promise<DutyIncompleteRecord[]> => {
    const response = await apiRequest('/api/duty-schedules/incomplete-records');
    return response.data || [];
  },

  // 删除未完成值日记录（需值日管理权限）
  deleteIncompleteRecord: async (recordId: string): Promise<void> => {
    await apiRequest(`/api/duty-schedules/incomplete-records/${recordId}`, {
      method: 'DELETE'
    });
  },

  // 获取值日成员组列表
  getDutyGroups: async (): Promise<DutyGroup[]> => {
    const response = await apiRequest('/api/duty-groups');
    return response.data;
  },

  // 创建值日成员组
  createDutyGroup: async (data: { name: string; description?: string; memberIds: string[] }): Promise<DutyGroup> => {
    const response = await apiRequest('/api/duty-groups', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },
};

